(() => {
  const galleries = new Map();
  const reducedMotion = () => matchMedia('(prefers-reduced-motion: reduce)').matches;
  const setHidden = (element, value) => { if (element.hidden !== value) element.hidden = value; };

  function initialize(root) {
    if (galleries.has(root)) return;
    let config;
    try { config = JSON.parse(root.querySelector('[data-hatchill-media-config]')?.textContent || 'null'); } catch { return; }
    if (!config?.labels) return;
    const labels = config.labels;
    const controller = new AbortController();
    const listen = (target, event, handler, options = {}) => target?.addEventListener(event, handler, { ...options, signal: controller.signal });
    let frame, viewer, modulePromise, toolbar, pointer;
    let opening = false, disposed = false, blockClickUntil = 0, moduleAttempts = 0;
    const historyKey = 'hatchillProductViewer';
    const historyId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    let lastViewerSlide, lastViewerTrigger;
    let historyClosing = false, pendingReopen;
    listen(window, 'popstate', () => {
      historyClosing = false;
      if (history.state?.[historyKey] === historyId) {
        if (!viewer && !opening && !disposed) openViewer(lastViewerSlide, lastViewerTrigger);
      } else if (viewer) viewer.close();
      if (pendingReopen && !viewer && !disposed) {
        const [slide, trigger] = pendingReopen; pendingReopen = null; openViewer(slide, trigger);
      }
    });
    const gallery = () => root.querySelector('.swiper-main');
    const component = () => root.querySelector('media-gallery swiper-component');
    const slides = () => [...(gallery()?.querySelectorAll('.swiper-slide') || [])]
      .filter(slide => !slide.hidden && getComputedStyle(slide).display !== 'none')
      .sort((a, b) => Number(a.dataset.swiperSlideIndex) - Number(b.dataset.swiperSlideIndex));
    const activeSlide = () => gallery()?.querySelector('.swiper-slide-active') || slides()[0];
    const getSwiper = () => component()?.initSwiper || gallery()?.swiper;
    const thumbs = () => [...root.querySelectorAll('.swiper-controls__thumbnail')].sort((a, b) => Number(a.dataset.index) - Number(b.dataset.index));
    const sizeGroup = () => root.querySelector('variant-selects[data-context="main"] [data-hatchill-size]');
    const purchaseSelector = '[id^="ProductSubmitButton-"], [id^="StickyCart-ProductSubmitButton-"]';
    let sizeConfirmed = !sizeGroup() || config.available === false ||
      new URL(location.href).searchParams.get('variant') === config.selectedVariant;
    const purchaseLabels = new Map();
    function refreshSizeIntent() {
      if (root.hasAttribute('data-size-pending') === sizeConfirmed) root.toggleAttribute('data-size-pending', !sizeConfirmed);
      if (sizeConfirmed) return;
      for (const group of root.querySelectorAll('[data-hatchill-size]')) {
        for (const input of group.querySelectorAll('input[type="radio"]')) {
          input.checked = false;
          if (input.hasAttribute('checked')) input.removeAttribute('checked');
          if (input.classList.contains('checked')) input.classList.remove('checked');
          if (input.nextElementSibling?.classList.contains('checked')) input.nextElementSibling.classList.remove('checked');
        }
        const select = group.querySelector('select');
        if (select) {
          let placeholder = select.querySelector('[data-size-placeholder]');
          if (!placeholder) {
            placeholder = document.createElement('option'); placeholder.value = '';
            placeholder.disabled = true; placeholder.dataset.sizePlaceholder = '';
            placeholder.textContent = labels.chooseSize; select.prepend(placeholder);
          }
          for (const option of select.options) {
            const selected = option === placeholder;
            if (option.hasAttribute('selected') !== selected) option.toggleAttribute('selected', selected);
          }
          select.value = '';
        }
        const value = group.querySelector('[data-selected-value]');
        if (value && value.textContent !== labels.chooseSize) value.textContent = labels.chooseSize;
      }
      for (const button of root.querySelectorAll(purchaseSelector)) {
        const text = button.querySelector('.add-to-cart-text');
        if (text && !purchaseLabels.has(button)) purchaseLabels.set(button, text.textContent);
        if (text && text.textContent !== labels.chooseSize) text.textContent = labels.chooseSize;
        button.disabled = false;
      }
    }
    listen(root, 'change', event => {
      if (sizeConfirmed || !event.target.closest('[data-hatchill-size]')) return;
      // Native picker initialization is not a customer's size choice.
      if (!event.isTrusted) { event.stopImmediatePropagation(); schedule(); return; }
      sizeConfirmed = true; root.removeAttribute('data-size-pending');
      for (const [button, label] of purchaseLabels) {
        const text = button.querySelector('.add-to-cart-text'); if (text) text.textContent = label;
      }
      root.querySelector('[data-size-help]')?.remove();
    }, { capture: true });
    function requireSize(event) {
      if (sizeConfirmed) return;
      const purchase = event.type === 'submit' ? event.target.querySelector(purchaseSelector) : event.target.closest(purchaseSelector);
      if (!purchase) return;
      event.preventDefault(); event.stopImmediatePropagation();
      const group = sizeGroup();
      if (!group) return;
      let help = root.querySelector('[data-size-help]');
      if (!help) {
        help = document.createElement('p'); help.dataset.sizeHelp = ''; help.className = 'hatchill-option-status';
        help.setAttribute('role', 'status'); help.textContent = labels.chooseSizeHelp; group.after(help);
      }
      group.scrollIntoView({ block: 'center', behavior: reducedMotion() ? 'auto' : 'smooth' });
      group.querySelector('input:not(.disabled),select')?.focus({ preventScroll: true });
    }
    listen(root, 'click', requireSize, { capture: true });
    listen(root, 'submit', requireSize, { capture: true });

    function goTo(slide) {
      if (!slide) return;
      const index = Number(slide.dataset.swiperSlideIndex);
      const swiper = getSwiper();
      if (swiper && !swiper.destroyed) {
        if (swiper.params.loop && swiper.slideToLoop) swiper.slideToLoop(index, reducedMotion() ? 0 : 250);
        else {
          const position = [...swiper.slides].indexOf(slide);
          if (position >= 0) swiper.slideTo(position, reducedMotion() ? 0 : 250);
        }
      }
    }

    function syncMediaDots(currentSlides, selected) {
      const dots = toolbar.querySelector('[data-hatchill-media-dots]');
      if (!dots) return;
      const sequence = currentSlides.map(item => item.dataset.swiperSlideIndex).join(',');
      if (dots.dataset.sequence !== sequence) {
        dots.replaceChildren();
        for (const [i, item] of currentSlides.entries()) {
          const button = document.createElement('button'); button.type = 'button';
          button.dataset.hatchillMediaDot = item.dataset.swiperSlideIndex;
          button.setAttribute('aria-label', `${labels.viewer} ${i + 1}`);
          dots.append(button);
        }
        dots.dataset.sequence = sequence;
      }
      setHidden(dots, currentSlides.length < 2);
      for (const button of dots.querySelectorAll('button')) {
        const active = button.dataset.hatchillMediaDot === selected?.dataset.swiperSlideIndex;
        if (button.getAttribute('aria-pressed') !== String(active)) button.setAttribute('aria-pressed', String(active));
        if (active && dots.clientWidth) {
          const bounds = dots.getBoundingClientRect(), target = button.getBoundingClientRect();
          if (target.left < bounds.left || target.left + target.width > bounds.left + bounds.width) {
            dots.scrollBy({left: target.left - bounds.left - (bounds.width - target.width) / 2, behavior: reducedMotion() ? 'instant' : 'smooth'});
          }
        }
      }
    }

    function update() {
      frame = null;
      refreshSizeIntent();
      if (!gallery()) return;
      const slide = activeSlide();
      if (!toolbar?.isConnected) {
        const template = root.querySelector('[data-hatchill-gallery-controls]');
        if (!template) return;
        toolbar = template.content.firstElementChild.cloneNode(true);
        if (component()?.hasAttribute('data-hatchill-media-viewer')) {
          toolbar.classList.add('hatchill-gallery-controls--editorial');
          gallery().after(toolbar);
        } else gallery().append(toolbar);
      }
      const currentSlides = slides();
      const index = currentSlides.indexOf(slide);
      syncMediaDots(currentSlides, slide);
      const count = toolbar.querySelector('[data-hatchill-count]');
      const text = `${Math.max(0, index) + 1} / ${currentSlides.length}`;
      if (count.textContent !== text) count.textContent = text;
      setHidden(toolbar.querySelector('[data-hatchill-view]'), !slide?.matches('.product-media-container--image'));
      setHidden(toolbar.querySelector('.hatchill-gallery-paging'), currentSlides.length < 2);
      for (const [i, thumb] of thumbs().entries()) {
        thumb.setAttribute('role', 'button'); thumb.tabIndex = 0;
        thumb.setAttribute('aria-label', `${labels.image} ${i + 1}`);
        const selected = Number(thumb.dataset.index) === Number(slide?.dataset.swiperSlideIndex);
        if (thumb.getAttribute('aria-pressed') !== String(selected)) thumb.setAttribute('aria-pressed', String(selected));
      }
      for (const [i, item] of currentSlides.entries()) {
        const content = item.querySelector('.slideshow__slide');
        if (content) content.setAttribute('aria-hidden', String(item !== slide));
        if (!item.matches('.product-media-container--image[data-zoom-target]')) continue;
        const editorial = toolbar.classList.contains('hatchill-gallery-controls--editorial');
        item.tabIndex = item === slide ? 0 : -1;
        item.setAttribute('role', editorial ? 'group' : 'button');
        item.setAttribute('aria-label', editorial ? `${labels.image} ${i + 1}` : `${labels.view}: ${labels.image} ${i + 1}`);
        if (!editorial) item.setAttribute('aria-haspopup', 'dialog');
      }
      const selectedUnavailable = [...root.querySelectorAll('variant-selects[data-context="main"] input:checked')].some(input => input.classList.contains('disabled') || input.dataset.optionAvailable === 'false');
      const picker = root.querySelector('variant-selects[data-context="main"]');
      let status = root.querySelector('[data-hatchill-option-status]');
      if (picker && !status) {
        status = document.createElement('p'); status.className = 'hatchill-option-status';
        status.dataset.hatchillOptionStatus = ''; status.setAttribute('role', 'status'); picker.after(status);
      }
      if (status) {
        const text = selectedUnavailable ? labels.unavailable : '';
        if (status.textContent !== text) status.textContent = text;
        setHidden(status, !selectedUnavailable);
      }
      const selectedSize = root.querySelector('variant-selects input:checked')?.value;
      for (const item of root.querySelectorAll('.hatchill-product-confidence__size-grid > [role="listitem"]')) {
        item.classList.toggle('hatchill-fit-selected', item.querySelector('strong')?.textContent.trim() === selectedSize);
      }
    }
    const schedule = () => { if (!frame) frame = requestAnimationFrame(update); };

    async function openViewer(slide, trigger) {
      if (opening || viewer || !slide) return;
      if (historyClosing) { pendingReopen = [slide, trigger]; return; }
      lastViewerSlide = slide; lastViewerTrigger = trigger;
      const currentSlides = slides().filter(s => s.matches('.product-media-container--image'));
      const images = currentSlides.map(s => {
        const image = s.querySelector('.product-media__image');
        const id = s.querySelector('[data-media-id]')?.dataset.mediaId;
        const source = config.images.find(item => item.id === id);
        return source && { ...source, msrc: image?.currentSrc || image?.src, element: s };
      }).filter(Boolean);
      const selected = images.findIndex(image => image.element === slide);
      if (selected < 0) return;
      opening = true;
      const status = toolbar?.querySelector('[data-hatchill-status]');
      if (status) { status.hidden = false; status.textContent = labels.loading; }
      try {
        if (!modulePromise) {
          const url = new URL(config.module, location.href);
          if (moduleAttempts) url.searchParams.set('hp_retry', String(moduleAttempts));
          modulePromise = import(url.href).catch(error => { modulePromise = null; moduleAttempts++; throw error; });
        }
        const { default: PhotoSwipe } = await modulePromise;
        if (disposed || !root.isConnected) return;
        viewer = new PhotoSwipe({
          dataSource: images, index: selected, mainClass: 'hatchill-product-viewer',
          bgOpacity: 1, loop: false, spacing: 0.08, preload: [1, 1],
          showHideAnimationType: reducedMotion() ? 'none' : 'fade',
          // PhotoSwipe rejects close while opening; instant opening keeps a fast
          // second tap or Escape responsive even before the first image decodes.
          showAnimationDuration: 0, hideAnimationDuration: 150, zoomAnimationDuration: reducedMotion() ? 0 : 200,
          paddingFn: viewport => ({ top: 64, bottom: viewport.x < 750 ? 100 : 112, left: viewport.x < 750 ? 0 : 64, right: viewport.x < 750 ? 0 : 64 }),
          initialZoomLevel: 'fit', secondaryZoomLevel: zoom => Math.min(1, zoom.fit * 2.5),
          maxZoomLevel: zoom => Math.max(1, zoom.fit * 4),
          closeTitle: labels.close, zoomTitle: labels.zoom, arrowPrevTitle: labels.previous, arrowNextTitle: labels.next,
          errorMsg: labels.imageError, imageClickAction: 'zoom', tapAction: 'toggle-controls', doubleTapAction: 'zoom',
          returnFocus: false, closeOnVerticalDrag: true, pinchToClose: false,
        });
        const openedViewer = viewer;
        const oldOverflow = document.body.style.overflow;
        const inertElements = [];
        openedViewer.on('uiRegister', () => {
          openedViewer.ui.registerElement({
            name: 'product-thumbnails', order: 20, isButton: false, appendTo: 'root',
            onInit: element => {
              element.setAttribute('aria-label', labels.viewer);
              const hint = document.createElement('p'); hint.className = 'hatchill-viewer-hint'; hint.textContent = labels.instruction; element.append(hint);
              const list = document.createElement('div'); list.className = 'hatchill-viewer-thumbnails'; list.dir = 'ltr'; element.append(list);
              images.forEach((image, index) => {
                const button = document.createElement('button'); button.type = 'button'; button.setAttribute('aria-label', `${labels.image} ${index + 1}`);
                const thumbnail = document.createElement('img'); thumbnail.src = image.msrc; thumbnail.alt = ''; thumbnail.width = 48; thumbnail.height = 60;
                button.append(thumbnail); button.addEventListener('click', () => openedViewer.goTo(index)); list.append(button);
              });
              openedViewer.on('change', () => {
                for (const [i, button] of [...list.children].entries()) button.setAttribute('aria-pressed', String(i === openedViewer.currIndex));
                const button = list.children[openedViewer.currIndex];
                if (button) list.scrollTo({ left: button.offsetLeft - list.offsetLeft - (list.clientWidth - button.offsetWidth) / 2, behavior: 'auto' });
              });
            },
          });
        });
        openedViewer.on('afterInit', () => {
          if (history.state?.[historyKey] !== historyId) {
            history.pushState({ ...history.state, [historyKey]: historyId }, '', location.href);
          }
          openedViewer.element.setAttribute('aria-label', labels.viewer);
          openedViewer.element.setAttribute('aria-modal', 'true');
          document.body.classList.add('hatchill-media-open'); document.body.style.overflow = 'hidden';
          openedViewer.updateSize(true);
          for (const element of document.body.children) {
            if (element !== openedViewer.element && !['SCRIPT', 'STYLE', 'LINK'].includes(element.tagName)) {
              inertElements.push([element, element.inert]); element.inert = true;
            }
          }
        });
        openedViewer.on('change', () => {
          const source = images[openedViewer.currIndex]?.element;
          if (source?.isConnected) { lastViewerSlide = source; goTo(source); }
        });
        openedViewer.on('destroy', () => {
          document.body.style.overflow = oldOverflow; document.body.classList.remove('hatchill-media-open');
          for (const [element, inert] of inertElements) element.inert = inert;
          viewer = null;
          if (history.state?.[historyKey] === historyId) {
            if (disposed) {
              const state = { ...history.state }; delete state[historyKey];
              history.replaceState(state, '', location.href);
            } else { historyClosing = true; history.back(); }
          }
          const returnTarget = trigger?.matches('.swiper-slide') ? activeSlide() : trigger;
          if (returnTarget?.isConnected) returnTarget.focus({ preventScroll: true });
        });
        openedViewer.init();
        if (status) { status.hidden = true; status.textContent = ''; }
      } catch {
        viewer?.destroy(); viewer = null;
        if (status) { status.hidden = false; status.textContent = labels.error; }
      } finally { opening = false; }
    }

    listen(root, 'pointerdown', event => {
      if (!event.target.closest('.swiper-main')) return;
      if (pointer) { blockClickUntil = performance.now() + 500; pointer = null; return; }
      pointer = { id: event.pointerId, x: event.clientX, y: event.clientY }; blockClickUntil = 0;
    });
    listen(root, 'pointermove', event => {
      if (pointer?.id === event.pointerId && Math.hypot(event.clientX - pointer.x, event.clientY - pointer.y) > 10) blockClickUntil = performance.now() + 500;
    });
    const endPointer = () => { pointer = null; };
    listen(window, 'pointerup', endPointer);
    listen(window, 'pointercancel', () => { pointer = null; blockClickUntil = performance.now() + 500; });
    listen(window, 'blur', endPointer);
    listen(root, 'click', event => {
      const dot = event.target.closest('[data-hatchill-media-dot]');
      if (dot) {
        event.preventDefault(); event.stopImmediatePropagation();
        goTo(slides().find(item => item.dataset.swiperSlideIndex === dot.dataset.hatchillMediaDot));
        return;
      }
      const thumb = event.target.closest('.swiper-controls__thumbnail');
      if (thumb) {
        event.preventDefault(); event.stopImmediatePropagation();
        if (thumb.closest('.swiper')?.swiper?.allowClick !== false) goTo(slides().find(s => Number(s.dataset.swiperSlideIndex) === Number(thumb.dataset.index)));
        return;
      }
      const slide = event.target.closest('.swiper-main .product-media-container--image[data-zoom-target]');
      if (slide) {
        const blocked = event.defaultPrevented || performance.now() < blockClickUntil || getSwiper()?.allowClick === false;
        event.preventDefault(); event.stopImmediatePropagation();
        if (!blocked) {
          if (component()?.hasAttribute('data-hatchill-media-viewer')) goTo(slide);
          else openViewer(slide, slide);
        }
        return;
      }
      if (event.target.closest('[data-hatchill-view]')) openViewer(activeSlide(), toolbar.querySelector('[data-hatchill-view]'));
      for (const [selector, offset] of [['[data-hatchill-previous]', -1], ['[data-hatchill-next]', 1]]) {
        if (event.target.closest(selector)) {
          const list = slides(); const index = list.indexOf(activeSlide()); goTo(list[(index + offset + list.length) % list.length]);
        }
      }
    }, { capture: true });
    listen(root, 'keydown', event => {
      const thumb = event.target.closest('.swiper-controls__thumbnail, [data-hatchill-media-dot]');
      const slide = event.target.closest('.swiper-main .product-media-container--image[data-zoom-target]');
      if (!thumb && !slide) return;
      if (['Enter', ' '].includes(event.key)) {
        event.preventDefault();
        if (thumb) goTo(slides().find(s => Number(s.dataset.swiperSlideIndex) === Number(thumb.dataset.hatchillMediaDot ?? thumb.dataset.index)));
        else openViewer(slide, slide);
      } else if (['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) {
        event.preventDefault();
        const list = slides(); const current = list.indexOf(activeSlide());
        const rtl = getComputedStyle(root).direction === 'rtl';
        const offset = (event.key === 'ArrowRight' ? 1 : -1) * (rtl ? -1 : 1);
        const index = event.key === 'Home' ? 0 : event.key === 'End' ? list.length - 1 : (current + offset + list.length) % list.length;
        goTo(list[index]);
        if (thumb) {
          const target = thumb.hasAttribute('data-hatchill-media-dot')
            ? toolbar.querySelector(`[data-hatchill-media-dot="${list[index]?.dataset.swiperSlideIndex}"]`)
            : thumbs().find(t => Number(t.dataset.index) === Number(list[index]?.dataset.swiperSlideIndex));
          target?.focus({ preventScroll: true });
        }
      }
    });
    listen(root, 'change', schedule);
    const observer = new MutationObserver(schedule);
    observer.observe(root, { subtree: true, childList: true, attributes: true, attributeFilter: ['class', 'hidden', 'checked'] });
    const pageObserver = new MutationObserver(() => { if (!root.isConnected) cleanup(); });
    pageObserver.observe(root.parentElement, { childList: true });
    let reviewObserver;
    function cleanup() {
      disposed = true; observer.disconnect(); pageObserver.disconnect(); controller.abort();
      reviewObserver?.disconnect();
      if (frame) cancelAnimationFrame(frame);
      viewer?.destroy(); galleries.delete(root);
    }
    galleries.set(root, cleanup); update();
    const navigation = document.createElement('nav'); navigation.className = 'hatchill-product-navigation'; navigation.setAttribute('aria-label', labels.navigation);
    for (const [selector, label] of [['.hatchill-product-content__description h2', labels.details], ['#whatstar-reviews', labels.reviews], ['product-recommendations', labels.related]]) {
      const target = document.querySelector(selector); if (!target) continue;
      target.id ||= `hatchill-product-${navigation.childElementCount}-${root.querySelector('product-info')?.dataset.productId}`;
      const link = document.createElement('a'); link.href = `#${target.id}`; link.textContent = label;
      listen(link, 'click', event => { event.preventDefault(); target.scrollIntoView({ behavior: reducedMotion() ? 'auto' : 'smooth', block: 'start' }); target.tabIndex = -1; target.focus({ preventScroll: true }); });
      navigation.append(link);
    }
    if (navigation.childElementCount) root.append(navigation);
    const reviews = document.querySelector('#whatstar-reviews');
    if (reviews) {
      const updateReviews = () => {
        const widget = reviews.querySelector('[data-wr-widget="product-reviews"]');
        const score = widget?.querySelector('.wr-widget__proof-score strong');
        const empty = widget?.dataset.wrState === 'loaded' && score &&
          Number(score.textContent.trim()) === 0 && Boolean(widget.querySelector('.wr-widget__empty')) &&
          !widget.querySelector('.wr-widget__card');
        setHidden(reviews, Boolean(empty));
        const link = navigation.querySelector('a[href="#whatstar-reviews"]');
        if (link) setHidden(link, Boolean(empty));
      };
      reviewObserver = new MutationObserver(updateReviews);
      reviewObserver.observe(reviews, { subtree: true, childList: true, attributes: true, attributeFilter: ['data-wr-state'] });
      updateReviews();
    }
  }

  function scan(scope) {
    // Reorder the merchant's own specification list; never infer missing facts.
    for (const body of scope.querySelectorAll('.hatchill-product-content__description .hatchill-product-content__body')) {
      const facts = [...body.children].find(child => child.matches('ul,ol') && child.children.length >= 2 &&
        [...child.children].every(item => item.matches('li') && item.querySelector('strong,b') && item.textContent.trim()));
      if (facts && !facts.classList.contains('hatchill-product-facts')) {
        facts.classList.add('hatchill-product-facts');
        body.prepend(facts);
      }
    }
    const roots = scope.matches?.('.hatchill-product-page') ? [scope] : scope.querySelectorAll('.hatchill-product-page');
    for (const root of roots) initialize(root);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => scan(document), { once: true });
  else scan(document);
  document.addEventListener('shopify:section:load', event => scan(event.target));
  document.addEventListener('shopify:section:unload', event => {
    for (const [root, cleanup] of galleries) if (event.target.contains(root)) cleanup();
  });
})();
